/**
 * Hooks React pour observer l'outbox.
 *
 * - `useOutboxCount()` : count global (total/pending/failed) qui se met à
 *   jour sur les notifyOutboxChanged().
 * - `useVacationPendingCount(vacationId)` : compteur ciblé par vacation
 *   (affiché sur la card pour signaler "X actions en attente").
 */
import { useEffect, useState } from "react";
import { onOutboxChanged } from "./network";
import { countOutbox, countOutboxByVacation } from "./outbox";

export interface OutboxCounts {
  total: number;
  pending: number;
  failed: number;
}

const EMPTY: OutboxCounts = { total: 0, pending: 0, failed: 0 };

export function useOutboxCount(): OutboxCounts {
  const [counts, setCounts] = useState<OutboxCounts>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const c = await countOutbox();
        if (!cancelled) setCounts(c);
      } catch {
        if (!cancelled) setCounts(EMPTY);
      }
    };
    void refresh();
    const off = onOutboxChanged(() => {
      void refresh();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  return counts;
}

export function useVacationPendingCount(vacationId: string): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const n = await countOutboxByVacation(vacationId);
        if (!cancelled) setCount(n);
      } catch {
        if (!cancelled) setCount(0);
      }
    };
    void refresh();
    const off = onOutboxChanged(() => {
      void refresh();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [vacationId]);

  return count;
}
